/**
 * Feedback capture form for an incorrect AI verdict.
 *
 * Offers two mutually exclusive kinds, chosen from the leading dropdown:
 *
 * - **Reference** — annotations captured directly from the clinical PDF
 *   (selectable text or a cropped image), persisted to `reviewer_references`.
 *   At least one annotation is required: the incorrect verdict is persisted
 *   only alongside a reference, and clearing the list reverts the criterion to
 *   unreviewed.
 * - **Comment** — free-text the reviewer types, persisted as a
 *   `reviewer_comments` entry. Saved explicitly; clearing it and saving also
 *   reverts the criterion to unreviewed.
 * @module CriterionFeedbackForm
 */
import { TagOption, Tags } from "@risalabs_frontend_org/oasis-ui-kit";
import React, { useEffect, useRef, useState } from "react";
import { NCCNEvaluationRecord } from "../../../data-model/nccnEvaluation";
import { EvaluationItem } from "../../../data-model/nccnGuidelines";
import { MedicalNecessityAnalytics } from "../analytics/useMedicalNecessityAnalytics";
import { usePdfProofCapture } from "../context/PdfProofCaptureContext";
import { PdfProof } from "../types/pdfProof";
import { FeedbackInput, ParsedItemFeedback } from "../utils/nccnReviewFeedback";
import { PdfProofList } from "./PdfProofList";

/** Props for {@link CriterionFeedbackForm}. */
interface CriterionFeedbackFormProps {
  initial: ParsedItemFeedback;
  isSaving: boolean;
  onCancel: () => void;
  onSave: (input: FeedbackInput) => void;
  /** Guideline the criterion belongs to (analytics location). */
  guideline: NCCNEvaluationRecord;
  /** One-based display index of the guideline. */
  guidelineIndex: number;
  /** The criterion being reviewed. */
  item: EvaluationItem;
  /** One-based index of the criterion within the guideline. */
  criterionIndex: number;
  /** Analytics facade for firing proof / comment events. */
  analytics: MedicalNecessityAnalytics;
}

/** Feedback kinds selectable from the form's leading dropdown. */
type SelectableKind = "incorrect_reference" | "incorrect_comment";

const KIND_LABELS: Record<SelectableKind, string> = {
  incorrect_reference: "Reference",
  incorrect_comment: "Comment",
};

const KIND_OPTIONS: TagOption[] = [
  {
    label: KIND_LABELS.incorrect_reference,
    value: "incorrect_reference",
    backgroundColor: "#F5F5F5",
    textColor: "#0F0F0F",
  },
  {
    label: KIND_LABELS.incorrect_comment,
    value: "incorrect_comment",
    backgroundColor: "#F5F5F5",
    textColor: "#0F0F0F",
  },
];

/**
 * Narrows a dropdown option value to a feedback kind this form can render.
 *
 * @param value - Raw `value` from the selected {@link TagOption}
 * @returns True when the value is a known {@link SelectableKind}
 */
const isSelectableKind = (value: unknown): value is SelectableKind =>
  value === "incorrect_reference" || value === "incorrect_comment";

/**
 * Builds a change key covering both the reference list and its comments.
 *
 * @param proofs - Confirmed references in the active session
 * @returns Key that changes whenever a reference or its comment is edited
 */
const buildProofsKey = (proofs: PdfProof[]): string =>
  proofs.map((proof) => `${proof.id}:${proof.comment ?? ""}`).join(",");

/**
 * Seeds the last-saved key so the sync effect writes exactly when the stored
 * verdict disagrees with the session's references.
 *
 * A stored "correct" verdict is always out of step with this form (the
 * reviewer just chose Incorrect), so it returns null — a value no proofs key
 * can equal — forcing an immediate write that clears it. Otherwise the stored
 * references are already in sync and nothing is written until they change.
 *
 * @param initial - Feedback currently stored on the criterion
 * @returns The in-sync proofs key, or null when a stored verdict must be cleared
 */
const resolveInitialSavedKey = (initial: ParsedItemFeedback): string | null =>
  initial.isCorrect ? null : buildProofsKey(initial.proofs);

/**
 * The free-text note currently persisted on the criterion, if any.
 *
 * Only an `incorrect_comment` verdict carries one; every other kind is treated
 * as having no stored comment, so switching to Comment starts from blank.
 *
 * @param initial - Feedback currently stored on the criterion
 * @returns The stored comment, trimmed, or an empty string
 */
const resolveStoredComment = (initial: ParsedItemFeedback): string =>
  initial.kind === "incorrect_comment" ? initial.comment.trim() : "";

/**
 * The kind the form should open on, so a saved comment reopens in Comment mode
 * and everything else defaults to the PDF reference flow.
 *
 * @param initial - Feedback currently stored on the criterion
 * @returns The initially selected dropdown kind
 */
const resolveInitialKind = (initial: ParsedItemFeedback): SelectableKind =>
  initial.kind === "incorrect_comment"
    ? "incorrect_comment"
    : "incorrect_reference";

/**
 * Renders the incorrect-answer feedback form.
 *
 * @param props - Existing references, saving flag, and cancel/save handlers
 * @returns The rendered form
 */
export const CriterionFeedbackForm: React.FC<CriterionFeedbackFormProps> = ({
  initial,
  isSaving,
  onSave,
  guideline,
  guidelineIndex,
  item,
  criterionIndex,
  analytics,
}) => {
  const {
    mode,
    proofs,
    isReviewing,
    isUploading,
    beginSession,
    startTextCapture,
    startScreenshotCapture,
    editProof,
    removeProof,
    cancelCapture,
    endSession,
    setAnalyticsContext,
  } = usePdfProofCapture();
  const initialProofsRef = useRef(initial.proofs);
  const lastSavedProofsKeyRef = useRef<string | null>(
    resolveInitialSavedKey(initial),
  );
  // Action id of the most recent capture click, handed to the provider so the
  // ensuing saved event can correlate back to the click.
  const pendingActionIdRef = useRef<string | undefined>(undefined);
  // Capture context starts empty; wait until beginSession has flushed before
  // treating [] as a reviewer-cleared list rather than pre-hydration state.
  const [sessionReady, setSessionReady] = useState(false);
  const [kind, setKind] = useState<SelectableKind>(() =>
    resolveInitialKind(initial),
  );
  const [commentText, setCommentText] = useState(initial.comment);
  const storedComment = resolveStoredComment(initial);

  useEffect(() => {
    beginSession(initialProofsRef.current);
    setSessionReady(true);
    return () => {
      setSessionReady(false);
      endSession();
    };
  }, [beginSession, endSession]);

  // Register this criterion's analytics location with the capture provider so
  // the Review modal's Save / Retake / Select Again can fire the saved and
  // reselect events with the correct criterion block.
  useEffect(() => {
    setAnalyticsContext({
      guideline,
      guidelineIndex,
      item,
      criterionIndex,
      setPendingActionId: (actionId) => {
        pendingActionIdRef.current = actionId;
      },
      getPendingActionId: () => pendingActionIdRef.current,
      trackAnnotationSaved: analytics.trackAnnotationSaved,
      trackScreenshotSaved: analytics.trackScreenshotSaved,
      trackScreenshotRetakeClicked: analytics.trackScreenshotRetakeClicked,
      trackAnnotationReselectClicked: analytics.trackAnnotationReselectClicked,
    });
    return () => setAnalyticsContext(null);
  }, [
    analytics,
    criterionIndex,
    guideline,
    guidelineIndex,
    item,
    setAnalyticsContext,
  ]);

  // An incorrect verdict is only recorded once a reference backs it: with no
  // references the criterion is written back as unreviewed, so the thumbs-down
  // chip can never imply a verdict that was not persisted.
  //
  // Scoped to the Reference kind so selecting Comment neither clears a stored
  // reference nor overwrites the comment the reviewer is still typing; each
  // kind is persisted only by its own explicit action.
  useEffect(() => {
    if (!sessionReady || kind !== "incorrect_reference") return;
    const proofsKey = buildProofsKey(proofs);
    if (proofsKey === lastSavedProofsKeyRef.current) return;
    lastSavedProofsKeyRef.current = proofsKey;
    onSave(
      proofs.length > 0
        ? { type: "incorrect_reference", proofs }
        : { type: "unreviewed" },
    );
  }, [kind, onSave, proofs, sessionReady]);

  const controlsDisabled = isSaving || isUploading || isReviewing;
  const hasProofs = proofs.length > 0;
  const isCommentKind = kind === "incorrect_comment";
  const trimmedComment = commentText.trim();
  const isCommentDirty = trimmedComment !== storedComment;

  /**
   * Switches the active feedback kind, leaving any PDF capture in progress.
   *
   * @param option - Option chosen from the kind dropdown
   */
  const handleKindSelect = (option: TagOption) => {
    const nextKind = option.value;
    if (!isSelectableKind(nextKind)) return;
    if (nextKind === "incorrect_comment") {
      cancelCapture();
      pendingActionIdRef.current = analytics.trackReferenceCommentSelected(
        guideline,
        guidelineIndex,
        item,
        criterionIndex,
      );
    }
    setKind(nextKind);
  };

  // Clearing a saved comment mirrors removing the last annotation: the
  // criterion goes back to unreviewed rather than keeping a verdict with no
  // reason attached to it.
  const handleCommentSave = () => {
    // `comment_saved` is emitted by the pending-feedback mechanism once the
    // draft commits (on Next/Back), so it only reports a save that happened.
    if (trimmedComment.length > 0) {
      pendingActionIdRef.current = undefined;
    }
    onSave(
      trimmedComment.length > 0
        ? { type: "incorrect_comment", comment: trimmedComment }
        : { type: "unreviewed" },
    );
  };

  const hintClassName =
    "font-['Lato'] text-[11px] font-semibold leading-[18px] text-[#0F0F0F] [font-feature-settings:'ss01'] [font-kerning:none]";
  const primaryButtonClassName =
    "flex h-[25px] items-center rounded bg-[#0F0F0F] px-2.5 text-[10px] font-bold text-white disabled:opacity-40";
  const secondaryButtonClassName =
    "flex h-[25px] items-center rounded border border-[#0F0F0F] bg-white px-2.5 text-[10px] font-bold text-[#0F0F0F] disabled:opacity-50";

  return (
    <div className="flex flex-col self-stretch rounded-md border border-[#E6E6E6]">
      <div className="flex flex-col gap-5 p-4">
        <div className="font-['Lato'] text-[11px] font-semibold leading-[16.5px] text-black">
          {isCommentKind
            ? "Add a comment explaining the correct answer"
            : "Upload or select proof of the correct answer"}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex h-[25px] items-center [&>*]:h-[25px] [&_button]:h-[25px] [&_button]:min-h-[25px]">
            <Tags
              label={KIND_LABELS[kind]}
              showBorder
              options={KIND_OPTIONS}
              shouldUsePortal={false}
              backgroundColor="#F5F5F5"
              textColor="#0F0F0F"
              onSelect={handleKindSelect}
              showSearch={false}
            />
          </div>
          {isCommentKind ? (
            <button
              type="button"
              onClick={handleCommentSave}
              disabled={controlsDisabled || !isCommentDirty}
              className={primaryButtonClassName}
            >
              Save Comment
            </button>
          ) : mode === "idle" ? (
            <button
              type="button"
              onClick={() => {
                pendingActionIdRef.current =
                  analytics.trackAnnotationClicked(
                    guideline,
                    guidelineIndex,
                    item,
                    criterionIndex,
                  );
                startTextCapture();
              }}
              disabled={controlsDisabled}
              className={primaryButtonClassName}
            >
              {hasProofs ? "Add More" : "Add Annotation"}
            </button>
          ) : mode === "text" ? (
            <span className={hintClassName}>Select Text on PDF</span>
          ) : (
            <>
              <span className={hintClassName}>
                Drag on pdf to capture Screenshot
              </span>
              <button
                type="button"
                onClick={cancelCapture}
                className={secondaryButtonClassName}
              >
                Cancel
              </button>
            </>
          )}
        </div>

        {isCommentKind && (
          <textarea
            value={commentText}
            onChange={(event) => setCommentText(event.target.value)}
            disabled={isSaving}
            rows={4}
            placeholder="Type your comment"
            className="resize-y self-stretch rounded border border-[#E6E6E6] p-2.5 font-['Lato'] text-[11px] font-normal leading-[16.5px] text-[#0F0F0F] placeholder:text-[#9C9C9C] focus:border-[#0F0F0F] focus:outline-none disabled:opacity-50"
          />
        )}

        {!isCommentKind && hasProofs && (
          <PdfProofList
            proofs={proofs}
            onEdit={editProof}
            onRemove={removeProof}
          />
        )}
      </div>

      {!isCommentKind && mode === "text" && (
        <div className="flex items-center justify-between border-t border-[#E6E6E6] px-4 py-4">
          <div className="flex items-center gap-2">
            <span className="font-['Lato'] text-[11px] font-semibold not-italic leading-[16.5px] text-black">
              Not able to select Text?
            </span>
            <button
              type="button"
              onClick={() => {
                pendingActionIdRef.current =
                  analytics.trackScreenshotClicked(
                    guideline,
                    guidelineIndex,
                    item,
                    criterionIndex,
                  );
                startScreenshotCapture();
              }}
              disabled={controlsDisabled}
              className="flex h-[25px] items-center gap-2.5 rounded border border-[#0F0F0F] bg-white px-2.5 py-0 text-[10px] font-bold text-[#0F0F0F] disabled:opacity-50"
            >
              Add Screenshot
            </button>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled
              className="flex h-[25px] items-center gap-2.5 rounded border-0 bg-[#0F0F0F] px-2.5 py-0 text-[10px] font-bold text-white disabled:opacity-40"
            >
              Review
            </button>
            <button
              type="button"
              onClick={cancelCapture}
              className="flex h-[25px] items-center gap-2.5 rounded border border-[#0F0F0F] bg-white px-2.5 py-0 text-[10px] font-bold text-[#0F0F0F] disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
